// src/hooks/useRecitations.js
import { useEffect, useState } from 'react';
import { fetchAudioRecitations } from '../api/quranService';

// Recordings with no ayah number (or ayah 0) cover the whole surah
const isWholeSurah = (rec) => !rec.ayah_index || String(rec.ayah_index) === '0';

// The media key each recording is served under (same layout the media pages always used)
const mediaKey = (rec) => (isWholeSurah(rec) ? rec.audio_url : `audio/${rec.audio_url}`);

const cache = new Map(); // surahId -> Promise<list>
const loadSurah = (surahId) => {
  if (!cache.has(surahId)) {
    // A surah without recordings answers 404; treat it as an empty list
    cache.set(surahId, fetchAudioRecitations(surahId).then((d) => d.ayah_list || []).catch(() => []));
  }
  return cache.get(surahId);
};

/**
 * Recordings for the given surahs by one reciter, as
 * { whole: {surahId: rec}, ayahs: {"2:255": rec}, loading }.
 * Each rec gains `mediaKey`, the path to play or download it.
 */
const useRecitations = (surahIds, reciterId) => {
  const [state, setState] = useState({ whole: {}, ayahs: {}, loading: true });
  const idsKey = (surahIds || []).join(',');

  useEffect(() => {
    let cancelled = false;
    const ids = idsKey ? idsKey.split(',') : [];
    setState((prev) => ({ ...prev, loading: true }));
    Promise.all(ids.map(loadSurah)).then((lists) => {
      if (cancelled) return;
      const whole = {};
      const ayahs = {};
      lists.flat()
        .filter((rec) => String(rec.audio_info_id) === String(reciterId))
        .forEach((rec) => {
          const item = { ...rec, mediaKey: mediaKey(rec) };
          if (isWholeSurah(rec)) whole[String(rec.surah_id)] = item;
          else ayahs[`${rec.surah_id}:${rec.ayah_index}`] = item;
        });
      setState({ whole, ayahs, loading: false });
    });
    return () => { cancelled = true; };
  }, [idsKey, reciterId]);

  return state;
};

export default useRecitations;
